import type { ServerEdit } from './buildClient';

/**
 * Changing a published web app from a sentence — the loop the product advertises.
 *
 * The editor's own seeded message says "tell me what to change and I'll update it", and
 * until now chat could not edit a web app at all. Worse than could not: it asked the model
 * for a brand-new ENGINE app and wrote the result over the record, so `source`, `runtime`,
 * `webUrl` and `hostedAppId` were destroyed and the app could never be republished again.
 * That was stopped, and people were sent to the Content tab instead. This restores the
 * loop properly, against the server's `/edit` route, which has existed the whole time with
 * nothing calling it.
 *
 * The ORDER is the safety property, the same one account deletion and content editing
 * each had to learn: the new source is written down BEFORE the live page is touched.
 * A publish that fails then costs somebody a page that is briefly out of date, which is
 * visible and recoverable. The other order costs them the edit, which is neither.
 */
export type WebEditOutcome =
  /**
   * Source saved and the page's document replaced. `offline` when the page is not being
   * served — taken offline by its owner — so the edit reaches nobody until it is put back
   * online, and saying "your live page is updated" would be untrue.
   */
  | { status: 'updated'; source: string; note?: string; offline?: true }
  /** Source saved; the page is behind. Retrying publishes it — the work is not lost. */
  | { status: 'saved_not_live'; source: string; message: string }
  /**
   * Nothing changed. `reason` is the server's refusal code (`insufficient_credits`,
   * `signup_required`, …) when the EDIT REQUEST itself was refused — see `runWebAppEdit`.
   */
  | { status: 'failed'; message: string; reason?: string };

export interface WebAppEditDeps {
  /** The app as it stands. Refused if empty — there is nothing to change. */
  source: string;
  instruction: string;
  /** What the app was originally for, so an edit reads as an edit and not a fresh build. */
  goal?: string;
  edit: (input: { source: string; instruction: string; goal?: string }) => Promise<ServerEdit>;
  /** Persist the new source locally. Must happen before the page is touched. */
  save: (source: string) => Promise<void>;
  /**
   * Push the saved source to the page. Answers `{ offline: true }` when the page it updated
   * is not being served, because a republish no longer switches a page back on.
   */
  publish: (source: string) => Promise<void | { offline?: boolean }>;
  /**
   * What the owner is told to do when the page could not be updated, after "Your change is
   * saved" is true and the page is behind.
   *
   * Each client names its OWN control. The phone's is the ⋯ menu's Update website, which it
   * offers exactly while the page is behind (`livePageBehind`); the web builder's is its
   * Publish changes button. A sentence written here for both named neither, and on the phone
   * it sent people to publish from a Preview card and a Content button that no longer exist.
   * Absent keeps the generic words the web builder has always shown.
   */
  publishAgain?: string;
}

/**
 * What an owner is told when an edit reached a page that is switched off.
 *
 * Saving is true and so is "updated", but "your live page is updated" is not: nobody can see
 * it. Named here, beside the outcome that carries `offline`, so chat and the editor's own
 * Update website say the same thing and point at the same place to switch it back on.
 */
export const SAVED_BUT_OFFLINE =
  "Saved. Your page is offline, so visitors won't see this until you put it back online from Your website.";

/** The phone's `publishAgain`: where Update website is, which is where the page is put right. */
export const PUBLISH_AGAIN_FROM_MENU =
  'Your change is saved. When you have a connection, tap ⋯ and choose Update website to put it live.';

function reason(e: unknown, fallback: string): string {
  const m = e instanceof Error ? e.message.trim() : '';
  return m || fallback;
}

export async function runWebAppEdit(deps: WebAppEditDeps): Promise<WebEditOutcome> {
  if (!deps.source.trim()) {
    return { status: 'failed', message: 'There is no app to change yet.' };
  }

  let result: ServerEdit;
  try {
    result = await deps.edit({ source: deps.source, instruction: deps.instruction, goal: deps.goal });
  } catch (e) {
    /*
     * The server's refusal code is carried, and ONLY from here.
     *
     * Keeping just the message turned an out-of-credits edit into "Could not generate — You
     * are out of credits." with a single OK: the editor routes a denial to the paywall or to
     * sign-up by its REASON, and the reason was dropped on the floor. Chat is the one live
     * edit path, so the person most likely to buy credits was the one never shown where.
     *
     * Not from `save` or `publish` below: those fail on this device or on the way to the
     * page, and a local write that failed must never send somebody to a paywall.
     */
    const code = (e as { reason?: unknown } | null)?.reason;
    return {
      status: 'failed',
      message: reason(e, 'That change could not be made. Try again.'),
      ...(typeof code === 'string' ? { reason: code } : {}),
    };
  }

  const next = (result?.appCode ?? '').trim();
  if (!next) {
    // An empty body would blank the app if it were saved. Refuse rather than write it.
    return { status: 'failed', message: 'That change came back empty, so nothing was altered.' };
  }

  try {
    await deps.save(next);
  } catch (e) {
    return { status: 'failed', message: reason(e, 'That change could not be saved.') };
  }

  /*
   * Carried, not swallowed. The server flags an edit that looks like it REPLACED the app —
   * "add a phone number field" coming back as a different product for a different person.
   * It reports rather than refuses because a sweeping instruction is legitimate, and only
   * the person who typed it knows which they meant. What must not happen is the swap going
   * out unremarked.
   */
  const note = result.drift?.reasons?.length
    ? `This looks like a bigger change than you asked for: ${result.drift.reasons.join('; ')}. If that is not what you wanted, tell me what to put back.`
    : undefined;

  let offline = false;
  try {
    const answer = (await deps.publish(next)) as { offline?: boolean } | undefined;
    offline = answer?.offline === true;
  } catch (e) {
    /*
     * A refusal about the app itself — taken down, made private — is not a connection problem,
     * and "publish again when you have a connection" would send somebody round in a circle.
     * The server's own sentence says what can help. See `HostRefusedError`.
     */
    const refused = (e as { refused?: unknown } | null)?.refused === true;
    return {
      status: 'saved_not_live',
      source: next,
      /*
       * The drift warning belongs here too, and was missing.
       *
       * Chat prints `message` alone on this branch, so a drifting edit that also failed to
       * publish said nothing about the drift — in the one case where it matters most, since
       * what is now SAVED may be a different app and the next retry will publish it.
       */
      message: [
        refused
          ? `Saved, but not live: ${reason(e, 'the server would not publish this page.')}`
          : `${reason(e, 'The live page could not be updated.')} ${deps.publishAgain ?? 'Your change is saved — publish again when you have a connection.'}`,
        note,
      ].filter(Boolean).join('\n\n'),
    };
  }

  return { status: 'updated', source: next, ...(note ? { note } : {}), ...(offline ? { offline: true as const } : {}) };
}
